import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { User, Mail, Shield, CalendarDays, Clock, RefreshCw, ChevronRight } from 'lucide-react';
import SpinWheel from './SpinWheel';
import PremiumPopup from './PremiumPopup';
import Avatar from './Avatar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ProfilePage() {
  const { user, logout, refreshUser } = useAuth();
  const [showAppointments, setShowAppointments] = useState(false);
  const [appointments, setAppointments] = useState({ upcoming: [], past: [] });
  const [loadingAppts, setLoadingAppts] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [hasFetched, setHasFetched] = useState(false);
  const [showWheel, setShowWheel] = useState(false);
  const [spinStatus, setSpinStatus] = useState(null);
  const [spinNewBalance, setSpinNewBalance] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [uploadFeedback, setUploadFeedback] = useState('');
  const [refreshing, setRefreshing] = useState(false); // For refresh button animation
  const fileInputRef = React.useRef(null);
  const [popup, setPopup] = useState({ isOpen: false, type: 'info', message: '' });

  const fetchAppointments = async () => {
    setLoadingAppts(true);
    try {
      const res = await axios.get(`${API}/appointments/me`, { withCredentials: true });
      setAppointments(res.data);
      setHasFetched(true);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingAppts(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/appointments/sync`, {}, { withCredentials: true });
      await fetchAppointments();
      window.dispatchEvent(new CustomEvent('loyalty:refresh', { detail: { source: 'appointments-sync' } }));
    } catch (e) {
      console.error(e);
    } finally {
      setSyncing(false);
    }
  };

  const handleOpenAppointments = () => {
    setShowAppointments(true);
    // Always fetch on open to get latest data
    fetchAppointments();
    // Also trigger Setmore sync in background
    handleSync();
  };

  const fetchSpinStatus = async () => {
    try {
      const res = await axios.get(`${API}/spin-wheel/status`, { withCredentials: true });
      setSpinStatus(res.data);
    } catch (e) {
      console.error('Failed to fetch spin status:', e);
    }
  };

  const refreshProfile = async () => {
    setRefreshing(true);
    try {
      // Refresh user data
      await refreshUser();
      
      // Refresh spin status
      await fetchSpinStatus();
      
      // If appointments are visible, refresh them too
      if (showAppointments) {
        await fetchAppointments();
      }
    } catch (e) {
      console.error('Failed to refresh profile:', e);
    } finally {
      setTimeout(() => setRefreshing(false), 300);
    }
  };

  const handleSpinResult = async (prize) => {
    try {
      const res = await axios.post(
        `${API}/spin-wheel/spin`,
        { prize_id: prize.id, prize_label: prize.label },
        { withCredentials: true }
      );
      // Store new balance to show in spin result
      if (res.data.new_balance !== undefined) {
        setSpinNewBalance(res.data.new_balance);
      }
      window.dispatchEvent(new CustomEvent('loyalty:refresh', { detail: { source: 'spin-wheel' } }));
      // Refresh spin status
      await fetchSpinStatus();
    } catch (e) {
      console.error('Failed to record spin:', e);
      throw e;
    }
  };

  useEffect(() => {
    if (user) {
      fetchSpinStatus();
    }
  }, [user]);

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadFeedback('');

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setPopup({ isOpen: true, type: 'error', message: 'Η εικόνα πρέπει να είναι μικρότερη από 5MB' });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setPopup({ isOpen: true, type: 'error', message: 'Μόνο εικόνες επιτρέπονται' });
      return;
    }

    setUploadingPicture(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(
        `${API}/users/profile-picture`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      await refreshUser();
      setUploadFeedback('Η φωτογραφία ενημερώθηκε επιτυχώς.');
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadFeedback(error.response?.data?.detail || 'Σφάλμα κατά την μεταφόρτωση');
    } finally {
      setUploadingPicture(false);
      e.target.value = '';
    }
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return '—';
    try {
      return new Date(isoStr).toLocaleDateString('el-GR', {
        weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
      });
    } catch { return isoStr; }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    try {
      return new Date(isoStr).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  if (showAppointments) {
    const list = activeTab === 'upcoming' ? appointments.upcoming : appointments.past;
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="appointments-view">
        {/* Back header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setShowAppointments(false)}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors text-sm font-light tracking-wide"
            data-testid="back-to-profile-btn"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
            <span>Προφίλ</span>
          </button>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center space-x-2 text-gray-500 hover:text-white transition-colors text-xs"
            data-testid="sync-appointments-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
            <span>{syncing ? 'Συγχρονισμός...' : 'Ανανέωση'}</span>
          </button>
        </div>

        <h2 className="text-3xl font-light tracking-wide">Τα Ραντεβού μου</h2>

        {/* Tabs */}
        <div className="flex space-x-1 border-b border-white/10">
          {[
            { id: 'upcoming', label: 'Επερχόμενα', count: appointments.upcoming.length },
            { id: 'past',     label: 'Ιστορικό',   count: appointments.past.length },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2.5 text-sm font-light tracking-wide border-b-2 -mb-px transition-all ${
                activeTab === tab.id
                  ? 'border-white text-white'
                  : 'border-transparent text-gray-500 hover:text-gray-300'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-2 text-xs text-gray-500">({tab.count})</span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        {loadingAppts ? (
          <div className="text-center text-gray-400 py-16 font-light">Φόρτωση ραντεβού...</div>
        ) : list.length === 0 ? (
          <div className="text-center py-16">
            <CalendarDays className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 font-light">
              {activeTab === 'upcoming' ? 'Δεν υπάρχουν επερχόμενα ραντεβού' : 'Δεν υπάρχει ιστορικό ραντεβού'}
            </p>
            <p className="text-gray-600 text-sm mt-2 font-light">
              Το email σας: {user?.email}
            </p>
          </div>
        ) : (
          <div className="space-y-4 stagger">
            {list.map((appt, i) => (
              <AppointmentCard key={appt.setmore_key || i} appt={appt} formatDate={formatDate} formatTime={formatTime} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 relative" data-testid="profile-page">
      {/* Refresh Button - Top Right */}
      <div className="absolute top-0 right-0 z-10">
        <button
          onClick={refreshProfile}
          disabled={refreshing}
          className="bg-white/5 hover:bg-white/10 border border-white/10 rounded p-1.5 transition-colors shadow-lg backdrop-blur-sm disabled:opacity-50"
          title="Ανανέωση προφίλ"
          data-testid="refresh-profile-btn"
        >
          <svg 
            width="16" 
            height="16" 
            fill="none" 
            stroke="white" 
            strokeWidth="2" 
            viewBox="0 0 24 24"
            className={refreshing ? 'animate-spin' : ''}
          >
            <path d="M23 4v6h-6M1 20v-6h6"/>
            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
          </svg>
        </button>
      </div>

      <PremiumPopup
        isOpen={popup.isOpen}
        onClose={() => setPopup({ ...popup, isOpen: false })}
        type={popup.type}
        message={popup.message}
      />

      {/* Profile Card */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <div className="flex items-center justify-center mb-4 relative">
            <Avatar 
              user={user} 
              size="lg"
              className="border-4 border-white/20"
            />
            
            {/* Upload button overlay */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPicture}
              className="absolute bottom-0 right-1/2 translate-x-1/2 translate-y-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full p-2 transition-all backdrop-blur-sm group"
              title="Αλλαγή φωτογραφίας"
              data-testid="profile-picture-upload-trigger"
            >
              {uploadingPicture ? (
                <RefreshCw className="w-4 h-4 text-white animate-spin" />
              ) : (
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
              {/* Tooltip */}
              <span className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                Αλλαγή φωτογραφίας
              </span>
            </button>
            
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleProfilePictureUpload}
              className="hidden"
              data-testid="profile-picture-input"
            />
          </div>
          <CardTitle className="text-white text-2xl text-center">{user?.name}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-3 text-gray-300">
            <Mail className="w-5 h-5" />
            <span>{user?.email}</span>
          </div>
          {user?.is_admin && (
            <div className="flex items-center space-x-3 text-green-400">
              <Shield className="w-5 h-5" />
              <span>Διαχειριστής (Admin)</span>
            </div>
          )}
          {uploadFeedback && (
            <div
              className={`border px-3 py-2 text-xs font-light ${uploadFeedback.includes('επιτυχώς') ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300' : 'border-red-500/20 bg-red-500/10 text-red-300'}`}
              data-testid="profile-picture-feedback"
            >
              {uploadFeedback}
            </div>
          )}
          <div className="pt-6 border-t border-white/10">
            <Button
              onClick={logout}
              variant="outline"
              className="w-full bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20"
              data-testid="profile-logout-button"
            >
              Αποσύνδεση
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* My Appointments Button */}
      <button
        onClick={handleOpenAppointments}
        className="w-full bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all p-5 flex items-center justify-between group"
        data-testid="my-appointments-btn"
      >
        <div className="flex items-center space-x-4">
          <div className="w-10 h-10 bg-white/10 flex items-center justify-center">
            <CalendarDays className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <p className="text-white font-light tracking-wide">Τα Ραντεβού μου</p>
            <p className="text-gray-500 text-xs mt-0.5 font-light">Επερχόμενα & ιστορικό</p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-white transition-colors" />
      </button>

      {/* Spin the Wheel Button */}
      <button
        onClick={() => {
          setShowWheel(true);
          // Scroll to top when opening wheel
          setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
        }}
        disabled={spinStatus && !spinStatus.can_spin}
        className={`w-full border transition-all p-5 flex items-center justify-between group ${
          spinStatus?.can_spin 
            ? 'bg-gradient-to-r from-amber-900/20 to-yellow-900/20 border-amber-700/30 hover:border-amber-600/50' 
            : 'bg-white/5 border-white/10 opacity-50 cursor-not-allowed'
        }`}
        data-testid="spin-wheel-btn"
      >
        <div className="flex items-center space-x-4">
          <div 
            className="w-10 h-10 flex items-center justify-center"
            style={{ color: '#D4AF37' }}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
            </svg>
          </div>
          <div className="text-left">
            <p className="font-light tracking-wide" style={{ color: '#D4AF37' }}>
              Spin the Wheel
            </p>
            <p className="text-gray-500 text-xs mt-0.5 font-light">
              {spinStatus?.can_spin 
                ? 'Διαθέσιμο τώρα!' 
                : spinStatus?.message || 'Κλείσε ραντεβού για να παίξεις'}
            </p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-white transition-colors" />
      </button>

      {/* Last Spin Result - Show when can't spin */}
      {!spinStatus?.can_spin && spinStatus?.reason === 'no_appointment' && (
        <div className="border border-amber-700/30 bg-gradient-to-r from-amber-900/20 to-yellow-900/20 p-5 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(212, 175, 55, 0.2)', border: '2px solid rgba(212, 175, 55, 0.4)' }}>
              <span className="text-xl">📅</span>
            </div>
            <div className="flex-1">
              <p className="text-white text-sm font-light">Πώς να ενεργοποιήσω τον τροχό;</p>
              <p className="text-amber-600 text-xs font-light mt-1">
                Κλείσε το επόμενο σου κούρεμα στο Setmore!
              </p>
            </div>
          </div>
          <div className="border-t border-amber-700/20 pt-3">
            <p className="text-gray-400 text-xs font-light leading-relaxed">
              Κάθε φορά που κλείνεις ραντεβού, ενεργοποιείται αυτόματα ο τροχός της τύχης για να κερδίσεις πόντους, εκπτώσεις ή δωρεάν υπηρεσίες!
            </p>
          </div>
        </div>
      )}
      
      {!spinStatus?.can_spin && spinStatus?.last_result && (
        <div className="border border-amber-700/20 bg-gradient-to-r from-amber-900/10 to-yellow-900/10 p-4 space-y-2">
          <p className="text-xs font-light tracking-wider uppercase text-amber-600">
            Τελευταίο Αποτέλεσμα
          </p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(212, 175, 55, 0.15)', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
                <span className="text-lg" style={{ color: '#D4AF37' }}>
                  {spinStatus.last_result.prize_id === 6 ? '🎉' : 
                   spinStatus.last_result.prize_id === 5 ? '✂️' :
                   spinStatus.last_result.prize_id === 4 ? '💰' :
                   spinStatus.last_result.prize_id === 3 ? '⭐' :
                   spinStatus.last_result.prize_id === 2 ? '✨' : '🎲'}
                </span>
              </div>
              <div>
                <p className="text-white text-sm font-light">{spinStatus.last_result.prize_label}</p>
                <p className="text-gray-500 text-xs font-light">
                  {new Date(spinStatus.last_result.timestamp).toLocaleDateString('el-GR', {
                    timeZone: 'Europe/Athens',
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>
            {spinStatus.last_result.prize_id !== 1 && (
              <span className="text-xs px-2 py-1 border font-light"
                style={{ color: '#D4AF37', borderColor: 'rgba(212, 175, 55, 0.3)', background: 'rgba(212, 175, 55, 0.1)' }}>
                ΚΕΡΔΟΣ
              </span>
            )}
          </div>
        </div>
      )}

      {/* Spin Wheel Modal */}
      {showWheel && (
        <SpinWheel 
          onClose={() => { setShowWheel(false); setSpinNewBalance(null); }} 
          onSpin={handleSpinResult}
          newBalance={spinNewBalance}
        />
      )}

      {/* Account Info */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white text-base font-light tracking-wide">Πληροφορίες Λογαριασμού</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-300 space-y-2">
          <p className="font-light text-sm">Ημερομηνία δημιουργίας: {new Date(user?.created_at).toLocaleDateString('el-GR')}</p>
          <p className="text-sm text-gray-400 font-light">Για αλλαγές στο προφίλ σας, επικοινωνήστε μαζί μας.</p>
        </CardContent>
      </Card>
    </div>
  );
}

function AppointmentCard({ appt, formatDate, formatTime }) {
  const isUpcoming = new Date(appt.start_time) > new Date();
  return (
    <div className="bg-white/5 border border-white/10 p-5 card-lift" data-testid="appointment-card">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-white font-light text-lg">{appt.service_name || 'Ραντεβού'}</h3>
        {isUpcoming && (
          <span className="text-xs bg-white/10 text-gray-300 px-2 py-1 border border-white/10 font-light tracking-wide">
            ΕΠΕΡΧΟΜΕΝΟ
          </span>
        )}
      </div>
      <div className="space-y-2 text-sm font-light">
        <div className="flex items-center space-x-2 text-gray-400">
          <CalendarDays className="w-4 h-4 flex-shrink-0" />
          <span>{formatDate(appt.start_time)}</span>
        </div>
        <div className="flex items-center space-x-2 text-gray-400">
          <Clock className="w-4 h-4 flex-shrink-0" />
          <span>{formatTime(appt.start_time)}{appt.duration ? ` · ${appt.duration} λεπτά` : ''}</span>
        </div>
        {appt.staff_name && (
          <div className="flex items-center space-x-2 text-gray-400">
            <User className="w-4 h-4 flex-shrink-0" />
            <span>{appt.staff_name}</span>
          </div>
        )}
        {appt.cost > 0 && (
          <div className="text-gray-400 pt-1 border-t border-white/5">
            {appt.cost} {appt.currency || 'EUR'}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProfilePage;
